import { ChangeEvent, useState } from 'react'
import {
  FormStatus,
  VpFormControl,
  VpFormError,
  VpIcon,
  VpIconButton,
  VpTextarea,
} from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { ActionChoiceDto } from '@/infra/api/generated/model'

interface CustomActionInputProps {
  action?: ActionChoiceDto
  onValidate: (value: string) => void
  onDelete: (action?: ActionChoiceDto) => void
  disabled?: boolean
}

export const CustomActionInput = ({
  disabled = false,
  onValidate,
  onDelete,
  action,
}: CustomActionInputProps) => {
  const { t } = useTranslation('common')
  const [value, setValue] = useState(action?.label || '')
  const [formStatus, setFormStatus] = useState<FormStatus>(undefined)

  const hasChanged = value.trim() !== (action?.label ?? '')

  const handleButtonClick = () => {
    if (formStatus === 'error') {
      return
    }
    const trimmedValue = value.trim()

    if (!trimmedValue || !hasChanged || disabled) {
      return
    }

    onValidate(trimmedValue)
    setValue('')
  }

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setValue(newValue)

    if (newValue.trim().length > 150) {
      setFormStatus('error')
    } else {
      setFormStatus('success')
    }
  }

  return (
    <div className="w-full flex gap-3 justify-between items-center">
      <VpFormControl className="flex-1 field-sizing-content" status={formStatus} required>
        <VpTextarea
          className="flex-1"
          placeholder={t('components.custom-action-input.placeholder')}
          value={value}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleButtonClick()
            }
          }}
        />
        {formStatus === 'error' && (
          <VpFormError>{t('components.custom-action-input.error')}</VpFormError>
        )}
      </VpFormControl>
      <VpIconButton
        size="small"
        variant="tertiary"
        variantStyle="regular"
        disabled={disabled}
        aria-label="Delete action"
        onClick={() => onDelete(action)}
      >
        <VpIcon name="delete-bin" />
      </VpIconButton>
      <VpIconButton
        size="small"
        variant="tertiary"
        variantStyle="regular"
        aria-label="Save action"
        onClick={handleButtonClick}
        disabled={disabled || !value.trim() || !hasChanged || formStatus === 'error'}
      >
        <VpIcon name="check" />
      </VpIconButton>
    </div>
  )
}
